import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Menu, Power } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { getCurrentCoordinates } from '../../utils/geolocation';
import { getCheckoutErrorMessage } from '../../utils/attendanceErrors';
import Sidebar from './Sidebar';
import Dashboard from './Dashboard';
import Attendance from './Attendance';
import MyTasks from './MyTasks';
import TaskDetail from './TaskDetail';
import LeaveApplication from './LeaveApplication';
import CompOffApplication from './CompOffApplication';
import Holidays from './Holidays';
import Profile from './Profile';
import MyTeam from './MyTeam';
import MyPerformance from './MyPerformance';
import MyAttendanceRequests from './MyAttendanceRequests';
import MyPayslips from './MyPayslips';
import WFHApplication from './WFHApplication';
import FullAndFinalSettlement from './FullAndFinalSettlement';
import Policies from './Policies';
import BirthdayPopup from '../common/BirthdayPopup';

const getAttendanceStatus = (todayAttendance) => {
  if (!todayAttendance || !todayAttendance.checkInTime) {
    return {
      color: 'bg-red-500',
      ring: 'ring-red-100',
      label: 'Not Checked In',
      textColor: 'text-red-700',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    };
  }
  if (todayAttendance.checkInTime && !todayAttendance.checkOutTime) {
    return {
      color: 'bg-orange-500',
      ring: 'ring-orange-100',
      label: 'Checked In',
      textColor: 'text-orange-700',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    };
  }
  return {
    color: 'bg-green-500',
    ring: 'ring-green-100',
    label: 'Checked Out',
    textColor: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  };
};

const formatAttendanceTime = (timeValue) => {
  if (!timeValue) return '--';
  return new Date(timeValue).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const EmployeeDashboard = () => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const saved = window.localStorage.getItem('employee-sidebar-collapsed');
    if (saved === 'true') {
      setSidebarCollapsed(true);
    }
  }, []);

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem('employee-sidebar-collapsed', String(next));
      return next;
    });
  };

  const fetchTodayAttendance = useCallback(async () => {
    try {
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istNow = new Date(now.getTime() + istOffset);
      const todayIST = istNow.toISOString().split('T')[0];
      const month = istNow.getUTCMonth() + 1;
      const year = istNow.getUTCFullYear();

      const response = await api.get(`/employee/attendance/history?month=${month}&year=${year}`);
      const todayRecord = response.data.attendances?.find(att => {
        const attDate = new Date(att.date);
        const attDateIST = new Date(attDate.getTime() + istOffset);
        return attDateIST.toISOString().split('T')[0] === todayIST;
      });
      setTodayAttendance(todayRecord || null);
    } catch {
      setTodayAttendance(null);
    }
  }, []);

  useEffect(() => {
    fetchTodayAttendance();
    const interval = setInterval(fetchTodayAttendance, 60000);
    return () => clearInterval(interval);
  }, [fetchTodayAttendance]);

  const broadcastAttendanceUpdate = () => {
    window.dispatchEvent(new Event('attendance-updated'));
  };

  const handleAttendanceToggle = async () => {
    if (actionLoading) {
      return;
    }

    if (!todayAttendance || !todayAttendance.checkInTime) {
      setActionLoading(true);
      try {
        await api.post('/employee/attendance/checkin');
        await fetchTodayAttendance();
        broadcastAttendanceUpdate();
        alert('Checked in successfully!');
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to check in');
      } finally {
        setActionLoading(false);
      }
      return;
    }

    if (!todayAttendance.checkOutTime) {
      setActionLoading(true);
      try {
        const coordinates = await getCurrentCoordinates();
        await api.post('/employee/attendance/checkout', coordinates);
        await fetchTodayAttendance();
        broadcastAttendanceUpdate();
        alert('Checked out successfully!');
      } catch (error) {
        alert(getCheckoutErrorMessage(error));
      } finally {
        setActionLoading(false);
      }
    }
  };

  const attendanceStatus = getAttendanceStatus(todayAttendance);
  const employeeIdentity = user?.employeeId || user?.email || 'Employee ID not available';
  const statusActionLabel = !todayAttendance || !todayAttendance.checkInTime
    ? 'Tap to check in'
    : !todayAttendance.checkOutTime
    ? 'Tap to check out'
    : 'Attendance completed';
  const isButtonDisabled = actionLoading || Boolean(todayAttendance?.checkOutTime);
  const checkInTimeLabel = formatAttendanceTime(todayAttendance?.checkInTime);
  const checkOutTimeLabel = formatAttendanceTime(todayAttendance?.checkOutTime);

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900">
      <BirthdayPopup />
      <Sidebar
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapsed}
      />
      
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-r from-white via-slate-50 to-sky-50 text-slate-900 shadow-[0_1px_0_rgba(15,23,42,0.04)] backdrop-blur-xl">
          <div className="pointer-events-none absolute inset-0">
            <div className="employee-header-orb employee-header-orb-a" />
            <div className="employee-header-orb employee-header-orb-b" />
          </div>
          <div className="relative z-10 flex items-center justify-between gap-4 px-4 py-3 sm:px-5 sm:py-4 lg:px-8">
            <div className="flex min-w-0 items-center gap-3 justify-self-start">
              <button
                onClick={() => setSidebarOpen(true)}
                className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-600 transition hover:-translate-y-0.5 hover:border-sky-300 hover:text-sky-700 hover:shadow-md lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>

              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-600">Welcome</p>
                <h1 className="truncate text-xl font-semibold text-slate-900 sm:text-2xl">
                  {user?.name || 'Employee'}
                </h1>
                <p className="truncate text-sm text-slate-500">Employee ID: {employeeIdentity}</p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-3 sm:gap-4">
              <button
                type="button"
                onClick={handleAttendanceToggle}
                disabled={isButtonDisabled}
                className={`flex items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-[0_12px_24px_rgba(15,23,42,0.08)] backdrop-blur-md transition ${attendanceStatus.bgColor} ${attendanceStatus.borderColor} ${isButtonDisabled ? 'cursor-not-allowed opacity-70' : 'hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(15,23,42,0.12)]'}`}
                title={statusActionLabel}
              >
                <span className={`flex h-10 w-10 items-center justify-center rounded-full border-2 border-white/80 shadow-inner ${attendanceStatus.color}`}>
                  <Power className="h-5 w-5 text-white" />
                </span>
                <div className="hidden sm:block text-left">
                  <p className={`text-sm font-semibold ${attendanceStatus.textColor}`}>{attendanceStatus.label}</p>
                  <p className="text-xs text-slate-500">{actionLoading ? 'Processing...' : statusActionLabel}</p>
                  <p className="text-[11px] text-slate-400">In: {checkInTimeLabel} | Out: {checkOutTimeLabel}</p>
                </div>
              </button>

              <button
                onClick={logout}
                className="rounded-xl border border-rose-200 bg-rose-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-rose-700 hover:shadow-[0_12px_24px_rgba(225,29,72,0.18)]"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="relative flex-1 overflow-x-hidden overflow-y-auto employee-bg-grid bg-slate-50 text-slate-900">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="employee-ambient-orb employee-ambient-orb-a" />
            <div className="employee-ambient-orb employee-ambient-orb-b" />
          </div>

          <div className="mx-auto w-full max-w-[1600px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
            <div className="relative rounded-[2rem] border border-slate-200 bg-white/90 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur-xl employee-shell-enter sm:p-6 lg:p-8">
              <div className="pointer-events-none absolute inset-0 employee-noise-overlay" />
              <div className="pointer-events-none absolute left-0 right-0 top-0 h-[3px] employee-sheen-band" />
              <div className="relative z-10">
                <Routes>
                  <Route path="/" element={<Navigate to="/employee/dashboard" />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/my-team" element={<MyTeam />} />
                  <Route path="/attendance" element={<Attendance />} />
                  <Route path="/tasks" element={<MyTasks />} />
                  <Route path="/tasks/:id" element={<TaskDetail />} />
                  <Route path="/performance" element={<MyPerformance />} />
                  <Route path="/leave-application" element={<LeaveApplication />} />
                  <Route path="/comp-off" element={<CompOffApplication />} />
                  <Route path="/holidays" element={<Holidays />} />
                  <Route path="/attendance-requests" element={<MyAttendanceRequests />} />
                  <Route path="/payslips" element={<MyPayslips />} />
                  <Route path="/policies" element={<Policies />} />
                  <Route path="/wfh" element={<WFHApplication />} />
                  <Route path="/full-and-final" element={<FullAndFinalSettlement />} />
                </Routes>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default EmployeeDashboard;
